import { AdminRole } from "@repo/api-dtos"
import { useNavigate } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"
import * as React from "react"
import { StatusDot, StatusTag, type StatusTone } from "@/components/ui/status"
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { cn } from "@/utils/styles.helpers"

export const MODEL_TYPES = [
	"classifier",
	"embedder",
	"set_symbols_classifier",
	"set_symbols_embedder",
	"fab_classifier",
	"language_classifier",
	"finish_classifier",
	"set_code_reader",
] as const
export type ModelType = (typeof MODEL_TYPES)[number]

export const MODEL_TYPE_LABELS: Record<ModelType, string> = {
	classifier: "Classifier",
	embedder: "Embedder",
	set_symbols_classifier: "Set symbols classifier",
	set_symbols_embedder: "Set symbols embedder",
	fab_classifier: "FAB classifier",
	language_classifier: "Language classifier",
	finish_classifier: "Finish classifier",
	set_code_reader: "Set-code reader",
}

/** The cnx role each model type trains — the key the eval seam uses. */
export const MODEL_TYPE_ROLES: Record<ModelType, string> = {
	classifier: "card-segmentation",
	embedder: "card-recognition",
	set_symbols_classifier: "set-symbol-detector",
	set_symbols_embedder: "set-symbol-recognition",
	fab_classifier: "color-bar",
	language_classifier: "card-language",
	finish_classifier: "card-finish",
	set_code_reader: "set-code-reader",
}

export const ROLE_LABELS: Record<string, string> = {
	"card-segmentation": "Segmenter",
	"card-recognition": "Embedder",
	"set-symbol-detector": "Set-symbol detector",
	"set-symbol-recognition": "Set-symbol embedder",
	"color-bar": "FAB colour",
	"card-language": "Language",
	"card-finish": "Finish",
	"set-code-reader": "Set code",
}

/** Human label for a cnx role; falls back to the role itself. */
export function roleLabel(role: string): string {
	return ROLE_LABELS[role] ?? role
}

export const EVAL_JOB_STATUSES = [
	"queued",
	"initializing",
	"downloading_data",
	"evaluating",
	"done",
	"failed",
] as const

export const EVAL_JOB_TRIGGERS = [
	"model_ingested",
	"suite_pinned",
	"manual",
] as const

export const JOB_STATUSES = [
	"initializing",
	"downloading_data",
	"augmenting_data",
	"training",
	"evaluating",
	"done",
	"failed",
] as const

export const TERMINAL_JOB_STATUSES = ["done", "failed"]

/** Gate a card-scanner page on the scanner:admin role (superadmin passes). */
export function useScannerGate(): boolean {
	const { hasRole } = useAdminRolesContext()
	const navigate = useNavigate()
	const allowed = hasRole(AdminRole.ScannerAdmin)

	React.useEffect(() => {
		if (!allowed) {
			navigate({ to: "/unauthorized" })
		}
	}, [allowed, navigate])

	return allowed
}

const ACTIVE_STATUSES = new Set([
	"running",
	"initializing",
	"downloading_data",
	"augmenting_data",
	"training",
	"evaluating",
])

/**
 * A job or run status: a failure is a tinted tag (it needs someone), every
 * other state a dot with its label.
 */
export function StatusBadge({ status }: { status: string }) {
	const label = status.replaceAll("_", " ")

	if (status === "failed") {
		return <StatusTag tone="bad">{label}</StatusTag>
	}

	const tone: StatusTone =
		status === "done" ? "ok" : ACTIVE_STATUSES.has(status) ? "info" : "neutral"

	return (
		<StatusDot
			tone={tone}
			className={cn(status === "superseded" && "line-through")}
		>
			{label}
		</StatusDot>
	)
}

/** Relative time with the absolute instant on hover. */
export function Instant({ iso }: { iso: string | null }) {
	if (!iso) return <span className="text-muted-foreground">—</span>
	const date = new Date(iso)
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span>{formatDistanceToNow(date, { addSuffix: true })}</span>
			</TooltipTrigger>
			<TooltipContent>{date.toLocaleString()}</TooltipContent>
		</Tooltip>
	)
}

export function formatInstant(iso: string | null): string {
	if (!iso) return "—"
	return new Date(iso).toLocaleString()
}

export function formatDuration(
	startIso: string,
	endIso: string | null,
): string {
	const end = endIso ? new Date(endIso).getTime() : Date.now()
	const minutes = Math.max(
		0,
		Math.round((end - new Date(startIso).getTime()) / 60_000),
	)
	if (minutes < 60) return `${minutes}m`
	return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** Heartbeat freshness for in-flight jobs — red once past the 10 min contract. */
export function HeartbeatCell({
	iso,
	active,
}: {
	iso: string
	active: boolean
}) {
	if (!active) return <span className="text-muted-foreground">—</span>
	const ageMs = Date.now() - new Date(iso).getTime()
	const stale = ageMs > 10 * 60_000
	return (
		<span className={cn("tabular-nums", stale && "font-medium text-bad")}>
			{formatDistanceToNow(new Date(iso), { addSuffix: true })}
			{stale && " (stale)"}
		</span>
	)
}

export function QueryError({ what, error }: { what: string; error: unknown }) {
	const message = error instanceof Error ? error.message : String(error)
	return (
		<div className="rounded-lg bg-bad-soft px-4 py-3 text-bad">
			Failed to load {what}: {message}
		</div>
	)
}
